/**
 * Right-hand selection panel: unit card (single), formation summary (multi), or the contact
 * inspector for an enemy counter. The orders footer drafts and sends orders for the selection.
 * In a replay (`gameStore.replay`) the panel is read-only: any unit can be inspected, in its own
 * nation's colours, and there is no orders footer.
 */
import {
  type Courier,
  dayOf,
  hourOfDay,
  type IntelContact,
  LOW_SUPPLY,
  type Order,
  type OrderKind,
  type PlayerView,
  type Posture,
  type Unit,
} from '@friction/engine';
import { useEffect, useMemo, useState } from 'react';
import { bus } from '../../game/bus';
import {
  courierEta,
  cssColor,
  echelonOf,
  factionOf,
  formatAge,
  formatHHour,
  formatStamp,
  nextHour,
  orderLabel,
  placeName,
  postureLabel,
  statusLabel,
  tickAt,
  unitTypeBlurb,
  unitTypeLabel,
} from '../../game/format';
import { gameStore, isReadOnly, type MapTool, useGame } from '../../game/store';
import { type Translator, useT } from '../../i18n';
import { nationLabel, nationName, unitName, unitShort } from '../../i18n/names';
import { Bar, Chip, cx, Segmented, TextInput } from '../common/controls';
import { Icon, type IconName } from '../common/Icon';
import { Stamp } from '../common/Panel';
import { UnitCounter } from '../common/UnitCounter';
import { holdSelection } from './hotkeys';
import hud from './hud.module.css';
import s from './selection.module.css';

const ORDER_ICON: Record<OrderKind, IconName> = {
  move: 'move',
  attack: 'attack',
  hold: 'hold',
  retreat: 'retreat',
  posture: 'flag',
};
/** Hex directions 0..5 as compass points (the contact's heading). */
const DIRS = ['E', 'NE', 'NW', 'W', 'SW', 'SE'] as const;

function useMapCtx() {
  const map = useGame((g) => g.map);
  const grid = useGame((g) => g.grid);
  return map && grid ? { map, grid } : null;
}

// ---- order lines --------------------------------------------------------------------------------

function OrderLine({
  order,
  tick,
  transit,
  arrivesAt,
}: {
  order: Order;
  tick: number;
  transit?: Courier;
  /** Arrival tick of a telephone/radio order still on the wire. */
  arrivesAt?: number;
}) {
  const t = useT();
  const ctx = useMapCtx();
  const main =
    order.kind === 'posture'
      ? t(`sel.adoptPosture.${order.posture ?? 'balanced'}`)
      : order.kind === 'hold' || !ctx
        ? orderLabel(order.kind, t.locale)
        : t('sel.orderTo', {
            order: orderLabel(order.kind, t.locale),
            place: placeName(ctx, order.target, t.locale),
          });
  const meta: string[] = [];
  if (order.opName) meta.push(order.opName);
  if (order.executeAt > tick)
    meta.push(t('sel.hhourIn', { hhour: formatHHour(order.executeAt, t.locale), h: order.executeAt - tick }));
  if (transit)
    meta.push(
      t(transit.mode === 'motorcycle' ? 'sel.courier.motorcycle' : 'sel.courier.horse', {
        eta: courierEta(transit),
        age: formatAge(tick - transit.departedAt, t.locale),
      }),
    );
  else if (arrivesAt !== undefined)
    meta.push(t('sel.byWire', { channel: t(`channel.${order.channel}`), h: Math.max(1, arrivesAt - tick) }));
  else
    meta.push(
      t('sel.issued', {
        channel: t(`channel.${order.channel}`),
        stamp: formatStamp(order.issuedAt, t.locale),
      }),
    );
  const onTheWire = transit !== undefined || arrivesAt !== undefined;
  return (
    <div className={cx(s.order, order.kind === 'attack' && s.orderAttack, onTheWire && s.transit)}>
      <Icon name={transit ? 'courier' : ORDER_ICON[order.kind]} size={18} className={s.orderIcon} />
      <div>
        <div className={s.orderMain}>{main}</div>
        <div className={s.orderMeta}>{meta.join(' · ')}</div>
      </div>
    </div>
  );
}

function statusChips(u: Unit, t: Translator) {
  const chips = [];
  const tone =
    u.status === 'routed'
      ? 'danger'
      : u.status === 'awaiting'
        ? 'brass'
        : u.status === 'attacking'
          ? 'warn'
          : undefined;
  chips.push(
    <Chip key="st" tone={tone}>
      {statusLabel(u.status, t.locale)}
    </Chip>,
  );
  if (u.encircled)
    chips.push(
      <Chip key="enc" tone="danger" icon="focus">
        {t('sel.encircled')}
      </Chip>,
    );
  else if (u.supply < LOW_SUPPLY)
    chips.push(
      <Chip key="sup" tone="danger">
        {u.hoursOutOfSupply ? t('sel.noSupplyFor', { h: u.hoursOutOfSupply }) : t('sel.noSupply')}
      </Chip>,
    );
  if (u.battle >= 0)
    chips.push(
      <Chip key="bt" tone="warn" icon="swords">
        {t('sel.inBattle')}
      </Chip>,
    );
  if (u.type === 'decoy')
    chips.push(
      <Chip key="dc">
        {t('sel.dummy', {
          type:
            t.locale === 'en'
              ? unitTypeLabel(u.disguise ?? 'infantry', t.locale).toLowerCase()
              : unitTypeLabel(u.disguise ?? 'infantry', t.locale),
        })}
      </Chip>,
    );
  return chips;
}

// ---- single unit --------------------------------------------------------------------------------

function UnitCard({ unit }: { unit: Unit }) {
  const t = useT();
  const tick = useGame((g) => g.view?.tick ?? 0);
  const couriers = useGame((g) => g.view?.couriers);
  const dispatches = useGame((g) => g.view?.dispatches);
  const ctx = useMapCtx();
  const inTransit = couriers?.filter((c) => c.targetUnit === unit.id) ?? [];
  const onWire = dispatches?.filter((d) => d.order.unitId === unit.id) ?? [];
  const exp = Math.min(4, Math.floor(unit.experience * 5));
  const days = Math.floor((tick - unit.createdAt) / 24);
  return (
    <>
      <div className={s.body}>
        <div className={s.bars}>
          <Bar label={t('sel.strength')} value={unit.strength} tone="ink" />
          <Bar label={t('sel.org')} value={unit.org} tone="org" />
          <Bar label={t('sel.supply')} value={unit.supply} tone="supply" />
          <Bar label={t('sel.entrenched')} value={unit.entrench} tone="entrench" />
          <div className={s.exp}>
            <span className={s.expLabel}>{t('sel.experience')}</span>
            <span className={s.pips}>
              {[0, 1, 2, 3, 4].map((i) => (
                <span key={i} className={cx(s.pip, i <= exp && s.pipOn)} />
              ))}
            </span>
            <span className={s.expName}>{t(`exp.${exp as 0 | 1 | 2 | 3 | 4}`)}</span>
          </div>
        </div>

        <div className={s.block}>
          <div className={s.blockTitle}>{t('sel.currentOrder')}</div>
          {unit.order ? (
            <OrderLine order={unit.order} tick={tick} />
          ) : (
            <div className={s.none}>
              {t('sel.noOrders', {
                posture:
                  t.locale === 'en'
                    ? postureLabel(unit.posture, t.locale).toLowerCase()
                    : postureLabel(unit.posture, t.locale),
              })}
            </div>
          )}
        </div>
        {unit.queuedOrder && (
          <div className={s.block}>
            <div className={s.blockTitle}>{t('sel.awaiting')}</div>
            <OrderLine order={unit.queuedOrder} tick={tick} />
          </div>
        )}
        {inTransit.length + onWire.length > 0 && (
          <div className={s.block}>
            <div className={s.blockTitle}>{t('sel.inTransit')}</div>
            {inTransit.map((c) => (
              <OrderLine key={c.id} order={c.order} tick={tick} transit={c} />
            ))}
            {onWire.map((d) => (
              <OrderLine key={`w${d.order.id}`} order={d.order} tick={tick} arrivesAt={d.deliverAt} />
            ))}
          </div>
        )}
        <div className={s.hint}>
          {unitTypeBlurb(unit.type, t.locale)}{' '}
          {ctx && t('sel.position', { place: placeName(ctx, unit.hex, t.locale) })}{' '}
          {days > 0 && t('sel.daysInField', { n: days })}
        </div>
      </div>
      <OrderControls units={[unit]} />
    </>
  );
}

// ---- multi-selection ----------------------------------------------------------------------------

function MultiCard({ units }: { units: Unit[] }) {
  const t = useT();
  const color = useGame((g) => factionColor(g.view, units[0]?.faction));
  const avg = (f: (u: Unit) => number) => units.reduce((a, u) => a + f(u), 0) / units.length;
  return (
    <>
      <div className={s.body}>
        <div className={s.bars}>
          <Bar label={t('sel.strength')} value={avg((u) => u.strength)} tone="ink" />
          <Bar label={t('sel.org')} value={avg((u) => u.org)} tone="org" />
          <Bar label={t('sel.supply')} value={avg((u) => u.supply)} tone="supply" />
        </div>
        <div className={s.multiGrid}>
          {units.map((u) => (
            <button
              key={u.id}
              type="button"
              className={s.mini}
              title={t('sel.miniTitle', { name: unitName(u.name, t.locale) })}
              onClick={(e) => {
                const g = gameStore.getState();
                if (e.shiftKey) g.select(g.selection.filter((id) => id !== u.id));
                else g.select([u.id]);
              }}
            >
              <UnitCounter
                type={u.type === 'decoy' ? (u.disguise ?? 'infantry') : u.type}
                decoy={u.type === 'decoy'}
                color={color}
                size={40}
              />
              <span className={s.miniName}>
                {t.locale === 'en' ? u.name.replace(/ Division$/, '') : unitShort(u.name, t.locale)}
              </span>
              <span className={s.miniBars}>
                <span className={s.miniBar}>
                  <i style={{ width: `${u.strength * 100}%`, background: '#d9cfb8' }} />
                </span>
                <span className={s.miniBar}>
                  <i
                    style={{
                      width: `${u.org * 100}%`,
                      background: u.org > 0.66 ? '#7fa865' : u.org > 0.4 ? '#c9a13f' : '#c2412f',
                    }}
                  />
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
      <OrderControls units={units} />
    </>
  );
}

// ---- orders footer ------------------------------------------------------------------------------

type HPreset = 'none' | '6' | '12' | 'dawn' | 'custom';

function OrderControls({ units }: { units: Unit[] }) {
  const readOnly = useGame(isReadOnly);
  if (readOnly) return null;
  return <OrderFooter units={units} />;
}

function OrderFooter({ units }: { units: Unit[] }) {
  const t = useT();
  const tool = useGame((g) => g.tool);
  const draft = useGame((g) => g.draft);
  const tick = useGame((g) => g.view?.tick ?? 0);
  const hasRadio = useGame((g) => g.view?.me.research.unlocked.includes('radio') ?? false);
  const g = gameStore.getState();
  const [preset, setPreset] = useState<HPreset>(draft.hHour === null ? 'none' : 'custom');
  const [customDay, setCustomDay] = useState(dayOf(tick + 12));
  const [customHour, setCustomHour] = useState(5);

  // An H-hour in the past means nothing any more.
  useEffect(() => {
    if (draft.hHour !== null && draft.hHour <= tick) {
      g.setDraft({ hHour: null });
      setPreset('none');
    }
  }, [draft.hHour, tick, g]);

  const postures = new Set(units.map((u) => u.posture));
  const posture: Posture | '' = postures.size === 1 ? units[0].posture : '';

  const setTool = (next: MapTool) => {
    bus.emit('sound', { name: 'click' });
    g.setTool(tool === next ? 'select' : next);
  };
  const pickPreset = (p: HPreset) => {
    setPreset(p);
    const at =
      p === '6'
        ? tick + 6
        : p === '12'
          ? tick + 12
          : p === 'dawn'
            ? nextHour(tick, 5)
            : p === 'custom'
              ? tickAt(customDay, customHour)
              : null;
    g.setDraft({ hHour: at !== null && at > tick ? at : null });
  };
  const applyCustom = (day: number, hour: number) => {
    setCustomDay(day);
    setCustomHour(hour);
    const at = tickAt(day, hour);
    g.setDraft({ hHour: at > tick ? at : null });
  };

  const btn = (kind: MapTool | 'hold', label: string, key: string, icon: IconName) => (
    <button
      type="button"
      className={cx(s.orderBtn, kind === 'attack' && s.orderBtnAttack, tool === kind && s.orderBtnOn)}
      title={kind === 'hold' ? t('sel.hold.title') : t('sel.tool.title', { label, key })}
      onClick={() => (kind === 'hold' ? holdSelection() : setTool(kind))}
    >
      <Icon name={icon} size={17} />
      {label}
      <kbd>{key}</kbd>
    </button>
  );

  return (
    <div className={s.foot}>
      <div className={s.orderBtns} data-tut="orders">
        {btn('move', orderLabel('move', t.locale), 'M', 'move')}
        {btn('attack', orderLabel('attack', t.locale), 'A', 'attack')}
        {btn('hold', orderLabel('hold', t.locale), 'H', 'hold')}
        {btn('retreat', orderLabel('retreat', t.locale), 'R', 'retreat')}
      </div>
      <div className={s.ctlRow} data-tut="posture">
        <span className={s.ctlLabel}>{t('sel.posture')}</span>
        <Segmented<Posture | ''>
          small
          block
          value={posture}
          onChange={(p) => {
            if (!p) return;
            g.issueOrder('posture', units[0].hex, p);
            bus.emit('sound', { name: 'confirm' });
          }}
          options={(['aggressive', 'balanced', 'defensive'] as const).map((p) => ({
            value: p,
            label: postureLabel(p, t.locale),
            title: t(`posture.${p}.hint`),
          }))}
        />
      </div>
      <div className={s.ctlRow}>
        <span className={s.ctlLabel}>{t('sel.channel')}</span>
        <Segmented
          small
          block
          value={draft.channel}
          onChange={(c) => g.setDraft({ channel: c })}
          options={[
            { value: 'auto', label: t('sel.channel.auto'), title: t('sel.channel.auto.hint') },
            { value: 'courier', label: t('sel.channel.courier'), title: t('sel.channel.courier.hint') },
            {
              value: 'radio',
              label: hasRadio ? (
                t('sel.channel.radio')
              ) : (
                <>
                  <Icon name="lock" size={11} /> {t('sel.channel.radio')}
                </>
              ),
              title: hasRadio ? t('sel.channel.radio.hint') : t('sel.channel.radio.locked'),
              disabled: !hasRadio,
            },
          ]}
        />
      </div>
      <div className={s.ctlRow} data-tut="hhour">
        <span className={s.ctlLabel}>{t('sel.hhour')}</span>
        <Segmented<HPreset>
          small
          block
          value={draft.hHour === null ? 'none' : preset}
          onChange={pickPreset}
          options={[
            { value: 'none', label: t('sel.hhour.now'), title: t('sel.hhour.now.hint') },
            { value: '6', label: t('sel.hhour.plus', { h: 6 }) },
            { value: '12', label: t('sel.hhour.plus', { h: 12 }) },
            { value: 'dawn', label: t('sel.hhour.dawn'), title: t('sel.hhour.dawn.hint') },
            { value: 'custom', label: '…', title: t('sel.hhour.custom.hint') },
          ]}
        />
      </div>
      {preset === 'custom' && (
        <div className={s.ctlRow}>
          <span />
          <span className={s.custom}>
            {t('sel.hhour.day')}
            <TextInput
              mono
              value={String(customDay)}
              onChange={(v) => applyCustom(Math.max(1, Number(v.replace(/\D/g, '')) || 1), customHour)}
              aria-label={t('sel.hhour.dayAria')}
            />
            {t('sel.hhour.at')}
            <TextInput
              mono
              value={`${String(customHour).padStart(2, '0')}00`}
              onChange={(v) =>
                applyCustom(customDay, Math.min(23, Number(v.replace(/\D/g, '').slice(0, 2)) || 0))
              }
              aria-label={t('sel.hhour.hourAria')}
            />
          </span>
        </div>
      )}
      {draft.hHour !== null && (
        <div className={s.ctlRow}>
          <span />
          <span className={s.hhour}>
            <Icon name="clock" size={13} />{' '}
            {t('sel.hhourIn', { hhour: formatHHour(draft.hHour, t.locale), h: draft.hHour - tick })}
            {hourOfDay(draft.hHour) >= 20 || hourOfDay(draft.hHour) < 5 ? t('sel.hhour.night') : ''}
          </span>
        </div>
      )}
      <div className={s.ctlRow}>
        <span className={s.ctlLabel}>{t('sel.operation')}</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <TextInput
            value={draft.opName ?? ''}
            maxLength={32}
            placeholder={t('sel.operation.placeholder')}
            onChange={(v) => g.setDraft({ opName: v.trim() ? v : null })}
            style={{ height: 26, flex: 1, fontSize: 12 }}
            aria-label={t('sel.operation.aria')}
          />
          <button
            type="button"
            className={s.close}
            title={t('sel.operation.suggest')}
            onClick={() => {
              const names = t('op.names').split('|');
              g.setDraft({
                opName: t('op.prefix', { name: names[Math.floor(Math.random() * names.length)] }),
              });
            }}
          >
            <Icon name="dice" size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- contact inspector --------------------------------------------------------------------------

function ContactBody({ c }: { c: IntelContact }) {
  const t = useT();
  const tick = useGame((g) => g.view?.tick ?? 0);
  const ctx = useMapCtx();
  const age = tick - c.lastSeen;
  return (
    <div className={s.body}>
      <div className={s.levels}>
        {([1, 2, 3] as const).map((l, i) => (
          <span key={l} className={cx(s.level, c.level > i && s.levelOn)}>
            {t(`sel.level.${l}`)}
          </span>
        ))}
      </div>
      <div className={s.facts}>
        <span className={s.factKey}>{t('sel.lastSeen')}</span>
        <span className={s.factVal}>
          {c.visibleNow
            ? t('sel.observedNow')
            : t('sel.seenAgo', { age: formatAge(age, t.locale), stamp: formatStamp(c.lastSeen, t.locale) })}
        </span>
        <span className={s.factKey}>{t('sel.contactPosition')}</span>
        <span className={s.factVal}>
          {ctx ? placeName(ctx, c.hex, t.locale) : '—'}
          {!c.visibleNow && t('sel.believed')}
        </span>
        <span className={s.factKey}>{t('sel.type')}</span>
        <span className={s.factVal}>
          {c.exposedDecoy
            ? t('sel.dummyFormation')
            : c.type
              ? unitTypeLabel(c.type, t.locale)
              : t('sel.unknown')}
        </span>
        {c.heading >= 0 && (
          <>
            <span className={s.factKey}>{t('sel.heading')}</span>
            <span className={s.factVal}>{t('sel.headingVal', { dir: t(`compass.${DIRS[c.heading]}`) })}</span>
          </>
        )}
      </div>
      {c.level >= 3 && c.strength !== null && c.org !== null && !c.exposedDecoy ? (
        <div className={s.bars}>
          <Bar
            label={t('sel.strength')}
            value={c.strength}
            tone="ink"
            display={t('sel.estimate', { pct: t.pct(c.strength).replace(/\s/g, '') })}
          />
          <Bar
            label={t('sel.org')}
            value={c.org}
            tone="org"
            display={t('sel.estimate', { pct: t.pct(c.org).replace(/\s/g, '') })}
          />
        </div>
      ) : (
        <div className={s.hint}>{c.exposedDecoy ? t('sel.decoyHint') : t('sel.unknownStrength')}</div>
      )}
      {age > 24 && !c.visibleNow && (
        <div className={s.hint}>{t('sel.oldReport', { age: formatAge(age, t.locale) })}</div>
      )}
    </div>
  );
}

// ---- panel --------------------------------------------------------------------------------------

/** Colour of a unit's nation (own units: our colour; allies and replayed nations: theirs). */
function factionColor(view: PlayerView | null, faction: number | undefined): number {
  if (!view) return 0;
  if (faction === undefined || faction === view.faction) return view.me.color;
  return factionOf(view, faction)?.color ?? view.me.color;
}

export function SelectionPanel() {
  const t = useT();
  const readOnly = useGame(isReadOnly);
  const selection = useGame((g) => g.selection);
  const inspected = useGame((g) => g.inspectedContact);
  const view = useGame((g) => g.view);
  const units = useMemo(() => {
    if (!view) return [];
    const ids = new Set(selection);
    return view.units.filter((u) => ids.has(u.id));
  }, [view, selection]);
  const contact = inspected !== null ? view?.contacts.find((c) => c.unitId === inspected) : undefined;
  if (!view) return null;

  const clear = () => {
    const g = gameStore.getState();
    g.select([]);
    g.inspectContact(null);
  };
  const closeBtn = (
    <button type="button" className={s.close} title={t('ui.closeEsc')} onClick={clear}>
      <Icon name="close" size={14} />
    </button>
  );

  if (units.length === 1) {
    const u = units[0];
    const shown = u.type === 'decoy' ? (u.disguise ?? 'infantry') : u.type;
    return (
      <aside
        className={cx(hud.plate, s.panel)}
        data-tut="selection"
        key={`u${u.id}`}
        style={{ ['--fc' as string]: cssColor(factionColor(view, u.faction)) }}
      >
        <div className={s.head}>
          <UnitCounter
            className={s.counter}
            type={shown}
            decoy={u.type === 'decoy'}
            color={factionColor(view, u.faction)}
            size={62}
          />
          <div className={s.names}>
            <div className={s.designation}>{unitName(u.name, t.locale)}</div>
            <div className={s.typeLine}>
              {unitTypeLabel(u.type, t.locale)} · {echelonOf(shown)} · {postureLabel(u.posture, t.locale)}
            </div>
            <div className={s.chips}>{statusChips(u, t)}</div>
          </div>
          {closeBtn}
        </div>
        <UnitCard unit={u} />
      </aside>
    );
  }

  if (units.length > 1) {
    return (
      <aside
        className={cx(hud.plate, s.panel)}
        data-tut="selection"
        key="multi"
        style={{ ['--fc' as string]: cssColor(factionColor(view, units[0].faction)) }}
      >
        <div className={s.head}>
          <span />
          <div className={s.names}>
            <div className={s.designation}>{t('sel.formations', { n: units.length })}</div>
            <div className={s.typeLine}>
              {Object.entries(
                units.reduce<Record<string, number>>((a, u) => {
                  const label = unitTypeLabel(u.type, t.locale);
                  a[label] = (a[label] ?? 0) + 1;
                  return a;
                }, {}),
              )
                .map(([type, n]) => t('sel.typeCount', { n, type }))
                .join(' · ')}
            </div>
          </div>
          {closeBtn}
        </div>
        <MultiCard units={units} />
      </aside>
    );
  }

  if (contact) {
    const f = factionOf(view, contact.faction);
    const color = f?.color ?? 0x777777;
    return (
      <aside
        className={cx(hud.plate, s.panel)}
        data-tut="selection"
        key={`c${contact.unitId}`}
        style={{ ['--fc' as string]: cssColor(color) }}
      >
        <div className={s.head}>
          <UnitCounter
            className={s.counter}
            type={contact.level >= 2 ? contact.type : null}
            color={color}
            size={62}
            ghost={!contact.visibleNow}
            exposed={contact.exposedDecoy}
          />
          <div className={s.names}>
            <div className={s.designation}>
              {contact.name
                ? unitName(contact.name, t.locale)
                : contact.level >= 2 && contact.type
                  ? t(`unit.formation.${contact.type}`)
                  : t('unit.unidentifiedContact')}
            </div>
            <div className={s.typeLine}>
              {f
                ? t('sel.allegiance', { adj: nationLabel(f, t.locale), name: nationName(f, t.locale) })
                : t('sel.unknownAllegiance')}
            </div>
            <div className={s.chips}>
              {contact.visibleNow ? (
                <Chip tone="danger" icon="eye">
                  {t('sel.observed')}
                </Chip>
              ) : (
                <Chip>{t('sel.ghost', { age: formatAge(view.tick - contact.lastSeen, t.locale) })}</Chip>
              )}
              {view.battles.some(
                (b) => b.attackers.includes(contact.unitId) || b.defenders.includes(contact.unitId),
              ) && (
                <Chip tone="warn" icon="swords">
                  {t('sel.inBattle')}
                </Chip>
              )}
            </div>
          </div>
          {closeBtn}
          {contact.exposedDecoy && (
            <Stamp className={s.decoyStamp} rotate={-9}>
              {t('sel.decoyStamp')}
            </Stamp>
          )}
        </div>
        <ContactBody c={contact} />
        {!readOnly && (
          <div className={s.foot}>
            <div className={s.hint}>{t('sel.attackHint')}</div>
          </div>
        )}
      </aside>
    );
  }
  return null;
}
